import React from "react";

import { useNavigate } from "react-router-dom";

import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import DistributionSettingPreview from "@app/analytics/Integrations/components/DistributionSettingPreview";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import useConnectIntegrationToasts from "@app/analytics/Integrations/hooks/useConnectIntegrationToasts";
import {
  IntegrationDetailContent,
  List,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import * as IntegrationUtils from "@app/analytics/Integrations/utils";
import { MESSAGING_BOT_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import { withQueryParams } from "@app/common/utils";

import { Column } from "@rui/Stack";

const UserTeamsIntegrationConfig = () => {
  const navigate = useNavigate();

  const { getIntegration, isLoading, getEnableUrl } = useIntegrations();

  const { authorize, disconnect, enable } = useIntegration();
  const { userSettings } = useUserSettings();

  const integration = getIntegration({
    providerId: "microsoft_graph_teams_push",
    scope: "user",
  });

  const providerConfig =
    MESSAGING_BOT_PROVIDERS_BY_ID.microsoft_graph_teams_push;

  useConnectIntegrationToasts({
    integration,
    targetAction: providerConfig.connectAction,
  });

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleAuthorize = () => {
    authorize.mutate({
      providerId: integration.providerId,
      returnRoute: integration.route,
      scope: "user",
    });
  };

  const handleClickConnect = () => {
    const enableUrl = getEnableUrl({
      providerId: "microsoft_graph_teams_push",
    });

    enable.mutate(enableUrl, {
      onSuccess: () => {
        // simulate the connection success query params to trigger the toast
        navigate(
          withQueryParams(location.pathname, {
            action: providerConfig.connectAction,
            success: true,
          })
        );
      },
      onError: () => handleAuthorize(),
    });
  };

  const handleDisconnect = () => {
    disconnect.mutate(integration.providerId, {
      onError: () => {
        toast.error(
          `There was an error disconnecting ${integration.displayName}. Please try again or log into your ${integration.displayNameShort} account to disconnect Read.`
        );
      },
    });
  };

  const providerContent = PROVIDER_CONTENT[integration.providerId];

  const recapRecipients =
    IntegrationUtils.getMeetingRecapRecipientsDescriptionFromUserSettings({
      userSettings,
      provider: "teams",
    });
  const preReadRecipients =
    IntegrationUtils.getMeetingPreReadRecipientsDescriptionFromUserSettings({
      userSettings,
      provider: "teams",
    });
  const topicReportRecipients =
    IntegrationUtils.getTopicReportRecipientsDescriptionFromUserSettings({
      userSettings,
      provider: "teams",
    });

  const connectCta = {
    onClick: handleClickConnect,
    isLoading: enable.isPending || enable.isError || authorize.isPending,
  };

  return (
    <>
      <IntegrationDetailHeader
        cta={connectCta}
        documentationUrl={integration.documentationUrl}
        isDisconnecting={disconnect.isPending}
        onClickDisconnect={handleDisconnect}
        onReauth={handleAuthorize}
        provider={integration}
      >
        <Typography variant="bodySecondaryReg" color="shades.neutral.600">
          {providerContent.headerDescription ??
            providerContent.shortDescription}
        </Typography>
      </IntegrationDetailHeader>
      {integration.isConnected ? (
        <IntegrationDetailContent spacing={3}>
          <Column spacing={1}>
            {integration.providerEmail && (
              <Typography variant="bodyPrimaryMed" color="shades.neutral.800">
                {integration.providerEmail} is connected
              </Typography>
            )}
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              You have successfully connected Read to{" "}
              {integration.displayNameShort}. The Read App can automatically
              message meeting recaps to you and other meeting participants.
            </Typography>
          </Column>
          <Column spacing={1.5}>
            <Typography variant="bodySecondaryMed" color="shades.neutral.800">
              Your settings
            </Typography>
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              <List>
                <DistributionSettingPreview
                  settingName="meeting recaps"
                  recipients={recapRecipients}
                  integrationDisplayName={integration.displayNameShort}
                />
                <DistributionSettingPreview
                  settingName="meeting Pre-Reads"
                  recipients={preReadRecipients}
                  integrationDisplayName={integration.displayNameShort}
                />
                <DistributionSettingPreview
                  settingName="topic summaries"
                  recipients={topicReportRecipients}
                  integrationDisplayName={integration.displayNameShort}
                />
              </List>
            </Typography>
          </Column>
        </IntegrationDetailContent>
      ) : (
        <IntegrationNotConnected cta={connectCta} provider={integration} />
      )}
    </>
  );
};

export default UserTeamsIntegrationConfig;
